import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { container } from "tsyringe";
import { PencilSquareIcon } from "@heroicons/react/24/solid";
import ProjectResponseResource from "common/resources/Project/ProjectResponseResource";
import ProjectConfigResponseResource from "common/resources/Project/Config/ProjectConfig/ProjectConfigResponseResource";
import Button, { EButtonColor, EButtonVariant } from "components/elements/Button";
import Typography from "components/elements/Typography";
import HasSubscriptionPlan from "components/materials/HasSubscriptionPlan";
import I18n from "components/materials/I18n";
import ProjectLikeButton from "components/materials/ProjectLikeButon";
import RequireIsProjectOwner from "components/materials/RequireIsProjectOwner";
import ModuleConfig from "configs/ModuleConfig";
import { translateISO369 } from "utils/I18nText";
import classes from "./classes.module.scss";
import ContactButton from "components/pages/FeaturedProjects/components/ContactButton";
import ProjectCreativeSources from "../ProjectCreativeSources";

const modules = container.resolve(ModuleConfig).get().modules;

type IProps = {
	project: ProjectResponseResource;
	config: ProjectConfigResponseResource;
};

export default function ProjectContent(props: IProps) {
	const navigate = useNavigate();
	const { isDescriptionExpanded, toggleExtendedDescription } = useProjectDescription();

	const navigateToEditProject = useCallback(() => {
		const actualPath = window.location.pathname;
		navigate(modules.pages.EditProjectConfig.props.path.replace(":projectId", props.project.id).concat(`?redirectPath=${actualPath}`));
	}, [navigate, props]);

	return (
		<div className={classes["root"]}>
			<div className={classes["title-container"]}>
				<Typography type="h1" weight="bold">
					{translateISO369(props.config.title)}
				</Typography>

				{props.config.subtitle && (
					<Typography type="h4" weight="bold" color="inherit" className={classes["subtitle"]}>
						{translateISO369(props.config.subtitle)}
					</Typography>
				)}
			</div>

			<Typography type="p" weight="regular" size="medium" hide={!isDescriptionExpanded} className={classes["description"]}>
				{translateISO369(props.config.description)}
			</Typography>

			<div className={classes["buttons-container"]}>
				<div className={classes["left"]}>
					<Button variant={EButtonVariant.OUTLINED} color={EButtonColor.PRIMARY} onClick={toggleExtendedDescription}>
						{!isDescriptionExpanded && I18n.asset.pages.project.buttons.readMore}
						{isDescriptionExpanded && I18n.asset.pages.project.buttons.readLess}
					</Button>

					<ProjectLikeButton project={props.project} />
				</div>
				{props.config.contactEmail && (
					<ContactButton label={I18n.trslt(I18n.asset.pages.featuredProjects.friends.content.buttons.contact.label)} email={props.config.contactEmail} />
				)}
			</div>

			<HasSubscriptionPlan>
				<RequireIsProjectOwner>
					<Button
						variant={EButtonVariant.TEXT}
						color={EButtonColor.PRIMARY}
						startIcon={<PencilSquareIcon className={classes["no-stroke"]} />}
						onClick={navigateToEditProject}>
						{I18n.asset.pages.project.buttons.edit_project}
					</Button>
				</RequireIsProjectOwner>
			</HasSubscriptionPlan>

			<ProjectCreativeSources />
		</div>
	);
}

const useProjectDescription = () => {
	const [isDescriptionExpanded, setIsDescriptionExpanded] = useState<boolean>(false);

	const toggleExtendedDescription = useCallback(() => setIsDescriptionExpanded((prev) => !prev), []);

	return {
		isDescriptionExpanded,
		toggleExtendedDescription,
	};
};
